import { toast } from "sonner";
import { did, type DidAgentResponse, waitForDidAgentReady } from "./client";
import { resolveDidBackgroundUrl } from "./backgrounds";
import type { DidAgentBlock, ScenarioSourceItem } from "@/lib/powtoon-be/types";

/**
 * Read back the voice D-ID actually stored. The edge function resolves the
 * provider from the live catalog, so this is the authoritative value to
 * persist — never a locally assumed one.
 */
function storedVoice(agent?: DidAgentResponse) {
  const v = agent?.presenter?.voice;
  return {
    voice_id: v?.voice_id?.trim() || undefined,
    voice_provider: v?.type?.trim() || undefined,
  };
}

/**
 * Sync the scenario's D-ID agent. Mirrors `syncTavusPal`: PATCH when an
 * `agent_id` exists, POST otherwise.
 */
export async function syncDidAgent(input: {
  scenarioName: string;
  presenter_id: string;
  presenter_type?: "expressive";
  voice_id?: string;
  /** Background id from the D-ID background registry (`"none"` clears it). */
  background_id?: string;
  /** Hosted URL for an uploaded background (`background_id: "custom"`). */
  background_url?: string;
  /** Scenario locale (`en-US`); the provider/language are resolved server-side. */
  voice_locale?: string;
  system_prompt: string;
  persona?: string;
  knowledge?: string;
  instructions?: string;
  opening_text?: string;
  inputs?: ScenarioSourceItem[];
  existingAgentId?: string;
  /** Scenario-owned knowledge base to attach for retrieval, if any. */
  knowledge_id?: string;
  /** Answer strictly from the knowledge base. */
  knowledge_grounded?: boolean;
  /** Tool ids to attach to the agent. Always sent, even when empty. */
  tool_ids?: string[];
}): Promise<{ block: DidAgentBlock; retiredAgentId?: string }> {
  const {
    scenarioName,
    presenter_id,
    presenter_type,
    voice_id,
    background_id,
    background_url: custom_background_url,
    voice_locale,
    system_prompt,
    persona,
    knowledge,
    instructions,
    opening_text,
    inputs,
    existingAgentId,
    knowledge_id,
    knowledge_grounded,
    tool_ids,
  } = input;

  if (!presenter_id) {
    const msg = "Pick an avatar before saving this D-ID scenario.";
    toast.error(msg);
    throw new Error(msg);
  }
  if (!system_prompt.trim()) {
    const msg = "Add persona / knowledge before saving this D-ID scenario.";
    toast.error(msg);
    throw new Error(msg);
  }

  const effectiveVoiceId = voice_id?.trim() || undefined;
  const finalSystemPrompt = system_prompt;
  // D-ID fetches the image from its own servers, so it needs an absolute URL.
  // We resolve it here (in the browser) instead of hardcoding a domain, so
  // self-hosted deployments serve the file from their own origin.
  const background_url = resolveDidBackgroundUrl(background_id, custom_background_url);

  try {
    if (existingAgentId) {
      const current = await did.getAgent(existingAgentId);
      const hasStoredBackground = Boolean(current.agent?.presenter?.background);
      // D-ID's PATCH schema has no valid representation for clearing a
      // presenter background (`null` is rejected). Replace only in that
      // transition; all other edits keep the scenario's stable agent id.
      if (background_id === "none" && hasStoredBackground) {
        const created = await did.createAgent({
          name: scenarioName,
          presenter_id,
          presenter_type,
          voice_id: effectiveVoiceId,
          background_id,
          voice_locale,
          system_prompt: finalSystemPrompt,
          persona,
          knowledge,
          instructions,
          opening_text,
          knowledge_id,
          knowledge_grounded,
          tool_ids,
        });
        const readyAgent = await waitForDidAgentReady(created.agent_id);
        return {
          block: {
            agent_id: created.agent_id,
            presenter_id,
            presenter_type,
            background_id,
            background_url: custom_background_url,
            ...storedVoice(readyAgent),
            system_prompt: finalSystemPrompt,
            opening_text,
            inputs,
          },
          retiredAgentId: existingAgentId,
        };
      }

      const res = await did.updateAgent({
        agent_id: existingAgentId,
        name: scenarioName,
        presenter_id,
        presenter_type,
        voice_id: effectiveVoiceId,
        background_id,
        // Re-sending a background makes D-ID regenerate the presenter. If it
        // is already attached, preserve it and avoid another unavailable window.
        background_url: hasStoredBackground ? undefined : background_url,
        voice_locale,
        system_prompt: finalSystemPrompt,
        persona,
        knowledge,
        instructions,
        opening_text,
        knowledge_id,
        knowledge_grounded,
        tool_ids,
      });
      const readyAgent = await waitForDidAgentReady(existingAgentId);
      return {
        block: {
          agent_id: existingAgentId,
          presenter_id,
          presenter_type,
          background_id,
          background_url: custom_background_url,
          ...storedVoice(readyAgent ?? res.agent),
          system_prompt: finalSystemPrompt,
          opening_text,
          inputs,
        },
      };
    }
    const res = await did.createAgent({
      name: scenarioName,
      presenter_id,
      presenter_type,
      voice_id: effectiveVoiceId,
      background_id,
      background_url,
      voice_locale,
      system_prompt: finalSystemPrompt,
      persona,
      knowledge,
      instructions,
      opening_text,
      knowledge_id,
      knowledge_grounded,
      tool_ids,
    });
    const readyAgent = await waitForDidAgentReady(res.agent_id);
    return {
      block: {
        agent_id: res.agent_id,
        presenter_id,
        presenter_type,
        background_id,
        background_url: custom_background_url,
        ...storedVoice(readyAgent ?? res.agent),
        system_prompt: finalSystemPrompt,
        opening_text,
        inputs,
      },
    };
  } catch (err) {
    const msg = (err as Error)?.message || "Could not sync agent to D-ID";
    toast.error(`Save failed: ${msg}`);
    throw err;
  }
}